import { useState, useEffect } from 'react';
import { ImageIcon } from 'lucide-react';

interface ProductImageCarouselProps {
  images: string[];
  alt: string;
  className?: string;
  placeholderClassName?: string;
  intervalMs?: number;
}

export function ProductImageCarousel({
  images,
  alt,
  className = 'w-full h-40 object-cover',
  placeholderClassName = 'w-full h-40 bg-muted flex items-center justify-center',
  intervalMs = 3000,
}: ProductImageCarouselProps) {
  const [current, setCurrent] = useState(0);
  const [failedIndexes, setFailedIndexes] = useState<Set<number>>(new Set());

  const validImages = images.filter((_, i) => !failedIndexes.has(i));

  useEffect(() => {
    setCurrent(0);
  }, [images.length]);

  useEffect(() => {
    if (validImages.length <= 1) return;
    const id = setInterval(() => setCurrent(i => (i + 1) % validImages.length), intervalMs);
    return () => clearInterval(id);
  }, [validImages.length, intervalMs]);

  if (validImages.length === 0) {
    return (
      <div className={placeholderClassName}>
        <ImageIcon className="h-12 w-12 text-muted-foreground/40" />
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      {validImages.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          className={className}
          style={{ display: i === current ? 'block' : 'none' }}
          onError={() => setFailedIndexes(prev => new Set(prev).add(images.indexOf(src)))}
        />
      ))}
      {validImages.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {validImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-1.5 h-1.5 rounded-full transition-[transform,background-color] duration-200 ease-out ${i === current ? 'bg-white scale-102' : 'bg-white/50'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
